// Thin typed client for the Stillhouse API. Same-origin in dev (Vite proxies
// /api -> the Fastify server).

export type MachineStatus = "ONLINE" | "IN_USE" | "OFFLINE" | "ERROR" | "MAINTENANCE" | "PROVISIONING";

export interface FleetMachine {
  id: string;
  hostname: string;
  spec: string | null;
  location: string | null;
  status: MachineStatus;
  assignedTo: string | null;
  assignedToId: string | null;
  team: string | null;
  lastSeenAt: string | null;
  apolloHostUuid: string | null;
}

export interface FleetSession {
  id: string;
  machine: string;
  operator: string;
  device: string | null;
  clientUuid: string | null;
  rttMs: number | null;
  bandwidthMbps: number | null;
  codec: string | null;
  hdr: boolean | null;
  resolution: string | null;
  fps: number | null;
  privacy: boolean;
  durationSec: number;
}

export interface Team {
  id: string;
  name: string;
  color: string | null;
}

// Org-wide user directory (GET /fleet/users) — feeds the assignment and
// CLIENT-token pickers.
export interface FleetUser {
  id: string;
  displayName: string;
  email: string;
  status: string;
  roleKey: string;
  teamId: string | null;
  teamName: string | null;
}

// Enrolled MoonShine Client devices (GET /client-devices).
export interface AccessRule {
  id: string;
  effect: "ALLOW" | "DENY";
  deviceId: string | null;
  deviceName: string | null;
  machineId: string | null;
  hostname: string | null;
  note: string | null;
  enabled: boolean;
  createdAt: string;
}

export interface AccessPair {
  deviceId: string;
  deviceName: string | null;
  machineId: string;
  hostname: string | null;
  allowed: boolean;
  reason: "allowed" | "denied-by-rule" | "no-matching-allow";
}

export interface ClientDevice {
  id: string;
  name: string;
  platform: string | null;
  userId: string | null;
  userName: string | null;
  enrolledAt: string;
  lastSeenAt: string | null;
  meshIp: string | null;
  peerState: string | null;
}

export interface UpdateInfo {
  currentVersion: string;
  checkedAt: string;
  latestVersion?: string | null;
  updateAvailable?: boolean;
  releaseName?: string | null;
  changelog?: string;
  releaseUrl?: string;
  publishedAt?: string | null;
  note?: string;
  error?: string;
}

export interface EnrollHostInput {
  baseUrl: string;
  controlToken: string;
  hostname?: string;
  teamId?: string;
  tlsFingerprint?: string;
}

export interface EnrollTokenResponse {
  tokenId: string;
  token: string;
  // MSP1 client pointer key (present for CLIENT/ANY tokens): pasted into
  // Moonlight's mesh enroll field to enroll a client device.
  msp1?: string;
  role: "HOST" | "CLIENT" | "ANY";
  expiresAt: string;
  deepLink: string;
  installCommand: { windows: string; macos: string; linux: string };
}

// ── Auth (contract: POST /auth/login, GET /auth/me, POST /auth/logout) ──

export type RoleKind = "OWNER" | "ADMIN" | "TEAM_LEAD" | "MEMBER" | "CONTRACTOR" | "CUSTOM";

// The API's permission catalog (apps/api/src/plugins/auth.ts + seed.ts). Keep
// these two lists identical — Roles.tsx renders this set as the capability
// matrix, so a key that exists here and nowhere else is a false promise.
export type PermissionKey =
  | "fleet.read"
  | "fleet.manage"
  | "session.disconnect"
  | "enroll.mint"
  | "mesh.manage"
  | "users.manage"
  | "settings.manage"
  | "audit.read";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  orgId: string;
  org: { id: string; name: string; plan: string; region: string | null };
  role: { key: string; kind: RoleKind };
  permissions: string[];
}

// ── Mesh (GET /mesh/status, POST /mesh/mode, GET /mesh/peers) ──

export type MeshMode = "OPEN" | "RESTRICTED";

export interface MeshStatus {
  configured: boolean;
  mode: MeshMode;
  networkId: string | null;
  managementUrl: string | null;
  hostsGroupId: string | null;
  clientsGroupId: string | null;
  openPolicyId: string | null;
  peers: { total: number; hosts: number; clients: number; pending: number };
}

export interface MeshPeer {
  id: string;
  kind: "HOST" | "CLIENT";
  peerId: string;
  pending: boolean;
  ip: string | null;
  lastSeen: string | null;
  setupKeyId: string | null;
  machine: { id: string; hostname: string } | null;
  device: { id: string; name: string } | null;
  user: { id: string; displayName: string; email: string } | null;
}

// ── Identity providers / Dex broker (GET/POST/PUT/DELETE /settings/idp*) ──

export type IdpType = "GOOGLE" | "MICROSOFT" | "OIDC";
export type IdpSyncStatus = "PENDING" | "APPLIED" | "ERROR";

export interface IdpProvider {
  id: string;
  type: IdpType;
  connectorId: string;
  displayName: string;
  clientId: string;
  hasClientSecret: boolean;
  tenantId?: string | null;
  issuer?: string | null;
  enabled: boolean;
}

export interface IdpBroker {
  issuerUrl: string;
  syncStatus: IdpSyncStatus;
  configVersion: number;
  appliedVersion: number;
  stillhouseLoginEnabled: boolean;
  clientAuthMode: string;
  lastError: string | null;
}

export interface IdpConfig {
  broker: IdpBroker;
  providers: IdpProvider[];
}

export interface IdpStatus {
  syncStatus: IdpSyncStatus;
  configVersion?: number;
  appliedVersion?: number;
  lastError?: string | null;
}

// Public login-page shape (GET /auth/sso/providers) — no secrets, just the
// enabled connectors the sign-in screen renders one button each for.
export interface SsoProvider {
  connectorId: string;
  displayName: string;
  type: IdpType;
}

export interface CreateIdpProviderInput {
  type: IdpType;
  displayName: string;
  clientId: string;
  clientSecret: string;
  tenantId?: string;
  issuer?: string;
}

// PUT semantics: an omitted key keeps the stored value; clientSecret is only
// re-encrypted when a non-empty value is sent (never round-tripped back).
export type UpdateIdpProviderInput = {
  displayName?: string;
  clientId?: string;
  clientSecret?: string;
  tenantId?: string;
  issuer?: string;
  enabled?: boolean;
};

// ── Session policy (GET/PUT /settings/session-policy — settings.manage) ──
//
// Two org-wide defaults, both enforced on the hosts as Apollo per-client
// permission bits. Nothing else is stored: a setting Stillhouse cannot push is
// not a setting, so the policy tab renders exactly these two.
export interface SessionPolicy {
  permClipboard: boolean;
  permFileTransfer: boolean;
}

// What the save actually reached. `failures` names the hosts still running the
// old permissions — the API retries them on its reconcile interval.
export interface SessionPolicyPush {
  hosts: number;
  applied: number;
  clientsUpdated: number;
  failures: { hostname: string; error: string }[];
}

// ── Audit trail (GET /audit, /audit/verify, /audit/export.csv — audit.read) ──

export interface AuditEvent {
  id: string;
  seq: number;
  event: string;
  target: string | null;
  detail: string | null;
  actorType: "USER" | "SYSTEM";
  actorId: string | null;
  actorLabel: string | null;
  actorIp: string | null;
  hash: string;
  prevHash: string | null;
  createdAt: string;
}

export interface AuditPage {
  events: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
  /** Retention window the API prunes to (config.auditRetentionDays). */
  retentionDays: number;
}

// Chain verification result (apps/api/src/lib/audit.ts verifyAuditChain).
export interface AuditChainStatus {
  ok: boolean;
  records: number;
  firstSeq: number | null;
  lastSeq: number | null;
  gaps: number[];
  brokenAtSeq: number | null;
  reason: string | null;
  /** Chain starts above seq 1 — expected after a retention prune. */
  truncatedPrefix: boolean;
  /** Records deleted from the newest end — no benign explanation. */
  truncatedTail: boolean;
  missingTail: number;
  anchorSeq: number | null;
  anchorHash: string | null;
  anchorBehind: boolean;
}

export interface AuditQuery {
  limit?: number;
  offset?: number;
  event?: string;
  from?: string;
  to?: string;
  q?: string;
}

function auditQueryString(q: AuditQuery = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(q)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

// Map the API's enum to the StatusDot keys used by the design system.
export const STATUS_KEY: Record<MachineStatus, string> = {
  ONLINE: "online",
  IN_USE: "in-use",
  OFFLINE: "offline",
  ERROR: "error",
  MAINTENANCE: "maintenance",
  PROVISIONING: "provisioning",
};

// ── Health / degraded mode (GET /health — public, no auth) ──
//
// The control plane boots without a working DATABASE_URL: the SPA loads, but
// every DB-backed route throws and the fleet renders empty. `database` is the
// API's authoritative answer to "is that emptiness real?" — see
// components/DegradedBanner.tsx. Both fields are optional so an older API that
// only returns {status,service,version,uptime} still type-checks; the banner
// treats an absent `database` as "unknown", not "healthy".
export interface HealthDatabase {
  /** DATABASE_URL is set at all. */
  configured: boolean;
  /** A preflight connectivity probe succeeded (false when configured but unreachable). */
  reachable: boolean;
  /** Operator-facing reason, when not reachable. Never a connection string. */
  error?: string | null;
}

export interface HealthStatus {
  status: string;
  service?: string;
  version?: string;
  uptime?: number;
  /** True when any subsystem is degraded — today that means the database. */
  degraded?: boolean;
  database?: HealthDatabase;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

// Session-expiry hook: the AuthProvider registers a handler so any 401 from a
// guarded endpoint drops the app back to the login screen. The login call is
// excluded — a bad-credentials 401 stays an inline form error.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

// Fallback degraded signal. GET /health is authoritative, but a deployment
// whose API predates the `database` field gives the browser nothing to read —
// and an unset/unreachable DATABASE_URL makes every DB-backed route throw,
// which Fastify returns as a 500. The banner falls back to this when, and only
// when, health reports no `database`. Sticky on purpose: a 5xx from the control
// plane stays worth showing until the operator reloads.
let lastServerError: string | null = null;
const serverErrorSubs = new Set<(msg: string) => void>();

export function getLastServerError(): string | null {
  return lastServerError;
}

export function onServerError(fn: (msg: string) => void): () => void {
  serverErrorSubs.add(fn);
  return () => {
    serverErrorSubs.delete(fn);
  };
}

function noteServerError(msg: string) {
  lastServerError = msg;
  for (const fn of serverErrorSubs) fn(msg);
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    ...init,
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* non-JSON error */
    }
    if (res.status === 401 && path !== "/auth/login" && onUnauthorized) onUnauthorized();
    if (res.status >= 500) noteServerError(msg);
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

export const api = {
  // Public liveness + degraded-mode probe. Deliberately unauthenticated: a
  // database that is down also blocks sign-in, so the login screen has to be
  // able to explain itself.
  health: () => req<HealthStatus>("/health"),
  login: (email: string, password: string) =>
    req<{ user: AuthUser }>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  // Contract: "same user shape" as login. Normalize both the {user} wrapper
  // and a bare user object so either reading of the contract works.
  me: async (): Promise<{ user: AuthUser }> => {
    const r = await req<{ user: AuthUser } | AuthUser>("/auth/me");
    return "user" in r ? r : { user: r };
  },
  logout: () => req<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  getMachines: () => req<{ machines: FleetMachine[]; total: number }>("/fleet/machines"),
  getFleetUsers: () => req<{ users: FleetUser[] }>("/fleet/users"),
  getClientDevices: () => req<{ devices: ClientDevice[] }>("/client-devices"),
  getAccessRules: () => req<{ rules: AccessRule[] }>("/access/rules"),
  getAccessEffective: () => req<{ meshMode: MeshMode; enforced: boolean; pairs: AccessPair[] }>("/access/effective"),
  createAccessRule: (body: { effect: "ALLOW" | "DENY"; deviceId: string | null; machineId: string | null; note?: string }) =>
    req<{ rule: AccessRule; meshSync?: string }>("/access/rules", { method: "POST", body: JSON.stringify(body) }),
  setAccessRuleEnabled: (id: string, enabled: boolean) =>
    req<{ rule: AccessRule; meshSync?: string }>(`/access/rules/${id}`, { method: "PATCH", body: JSON.stringify({ enabled }) }),
  deleteAccessRule: (id: string) => req<{ ok: boolean; meshSync?: string }>(`/access/rules/${id}`, { method: "DELETE" }),
  deleteClientDevice: (id: string) => req<{ ok: boolean }>(`/client-devices/${id}`, { method: "DELETE" }),
  getSessions: () => req<{ live: FleetSession[]; total: number }>("/fleet/sessions"),
  getTeams: () => req<{ teams: Team[] }>("/fleet/teams"),
  assignMachine: (id: string, userId: string | null) =>
    req<{ ok: boolean; machineId: string; assignedToId: string | null; meshSync?: string }>(
      `/fleet/machines/${id}/assign`,
      { method: "PATCH", body: JSON.stringify({ userId }) },
    ),
  getMeshStatus: () => req<MeshStatus>("/mesh/status"),
  setMeshMode: (mode: MeshMode) =>
    req<{ ok: boolean; mode: MeshMode; meshSync?: string }>("/mesh/mode", {
      method: "POST",
      body: JSON.stringify({ mode }),
    }),
  getMeshPeers: () => req<{ peers: MeshPeer[]; total: number }>("/mesh/peers"),
  // ── Identity providers (settings.manage) ──
  getIdp: () => req<IdpConfig>("/settings/idp"),
  createIdpProvider: (body: CreateIdpProviderInput) =>
    req<{ provider: IdpProvider }>("/settings/idp/providers", { method: "POST", body: JSON.stringify(body) }),
  updateIdpProvider: (id: string, body: UpdateIdpProviderInput) =>
    req<{ provider: IdpProvider }>(`/settings/idp/providers/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteIdpProvider: (id: string) => req<{ ok: boolean }>(`/settings/idp/providers/${id}`, { method: "DELETE" }),
  applyIdp: () => req<{ syncStatus: string; error?: string }>("/settings/idp/apply", { method: "POST" }),
  idpStatus: () => req<IdpStatus>("/settings/idp/status"),
  setStillhouseLogin: (enabled: boolean) =>
    req<{ enabled: boolean }>("/settings/idp/stillhouse-login", { method: "PUT", body: JSON.stringify({ enabled }) }),
  // Public: enabled broker connectors for the login page's per-provider buttons.
  ssoProviders: () => req<{ providers: SsoProvider[] }>("/auth/sso/providers"),
  // Public: legacy SsoConfig on/off, so the login page can show a generic SSO
  // fallback button when the broker has zero registered upstream providers.
  ssoEnabled: () => req<{ enabled: boolean }>("/auth/sso/enabled"),
  createEnrollToken: (body?: { teamId?: string; label?: string; role?: "HOST" | "CLIENT" | "ANY"; userId?: string }) =>
    req<EnrollTokenResponse>("/enroll/tokens", { method: "POST", body: JSON.stringify(body ?? {}) }),
  enrollHost: (body: EnrollHostInput) =>
    req<{ machine: { id: string; hostname: string; status: MachineStatus } }>("/fleet/hosts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  unenrollHost: (id: string) => req<{ ok: boolean }>(`/fleet/hosts/${id}`, { method: "DELETE" }),
  disconnectSession: (id: string) =>
    req<{ ok: boolean }>(`/fleet/sessions/${id}/disconnect`, { method: "POST" }),
  checkUpdates: () => req<UpdateInfo>("/updates/check"),
  // ── Session policy (settings.manage) ──
  getSessionPolicy: () => req<{ policy: SessionPolicy }>("/settings/session-policy"),
  updateSessionPolicy: (patch: Partial<SessionPolicy>) =>
    req<{ policy: SessionPolicy; push: SessionPolicyPush }>("/settings/session-policy", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  // ── Audit (audit.read) ──
  getAudit: (q: AuditQuery = {}) => req<AuditPage>(`/audit${auditQueryString(q)}`),
  verifyAudit: () => req<AuditChainStatus>("/audit/verify"),
  // CSV, not JSON — returned as text so the caller can hand it to the browser
  // as a download without a second round trip.
  exportAuditCsv: async (q: AuditQuery = {}): Promise<string> => {
    const res = await fetch(`/api/audit/export.csv${auditQueryString(q)}`, { credentials: "same-origin" });
    if (!res.ok) {
      if (res.status === 401 && onUnauthorized) onUnauthorized();
      throw new ApiError(`${res.status} ${res.statusText}`, res.status);
    }
    return res.text();
  },
};
